import React from 'react';

/**
 * Dropdown select field.
 *
 * Usage with react-hook-form:
 *   <SelectField label="Status *" id="status"
 *     register={register("status", { required: "Required" })}
 *     error={errors.status}>
 *     {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
 *   </SelectField>
 *
 * Usage controlled:
 *   <SelectField label="Quote Status" value={val} onChange={handler}>
 *     {options.map(...)}
 *   </SelectField>
 *
 * compact prop: smaller padding, rounded-xl — for inline/flex contexts.
 */

const FULL_CLS =
  'block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6';

const COMPACT_CLS =
  'rounded-xl border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm sm:leading-6';

export function SelectField({
  label,
  id,
  register,
  error,
  disabled,
  hint,
  compact = false,
  className = '',
  labelClassName = '',
  wrapperClassName = '',
  children,
  ...rest  // value, onChange, onBlur, etc.
}) {
  const base = compact ? COMPACT_CLS : FULL_CLS;
  const ring = error ? 'ring-red-500' : 'ring-gray-300';
  const dis = disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : '';

  return (
    <div className={wrapperClassName || (compact ? undefined : 'w-full')}>
      {label && (
        <label
          htmlFor={id}
          className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
        >
          {label}
        </label>
      )}
      <select
        id={id}
        {...(register || {})}
        disabled={disabled}
        className={[base, ring, dis, className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </select>
      {error && (
        <p className="mt-1 text-xs text-red-500">{error.message}</p>
      )}
      {hint && !error && (
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );
}
